import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Typography utility functions for consistent text styling
export const typography = {
  display: {
    large: "text-4xl font-bold leading-tight tracking-tight",
    default: "text-3xl font-bold leading-tight tracking-tight",
  },
  heading: {
    large: "text-2xl font-semibold leading-tight tracking-tight",
    default: "text-xl font-semibold leading-normal",
    small: "text-lg font-medium leading-normal",
  },
  body: {
    large: "text-lg font-normal leading-relaxed",
    default: "text-base font-normal leading-relaxed",
    small: "text-sm font-normal leading-normal",
  },
  label: "text-sm font-medium leading-normal",
  caption: "text-xs font-normal leading-normal text-muted-foreground",
} as const;

// Helper function to get typography classes
export function getTypographyClasses(
  variant: keyof typeof typography,
  size?: string
): string {
  const variantStyles = typography[variant];
  
  if (typeof variantStyles === "string") {
    return variantStyles;
  }
  
  if (size && size in variantStyles) {
    return variantStyles[size as keyof typeof variantStyles];
  }
  
  return variantStyles.default;
}

// Color utility functions
export const colors = {
  text: {
    primary: "text-foreground",
    secondary: "text-muted-foreground",
    accent: "text-primary",
    success: "text-green-600",
    warning: "text-yellow-600",
    error: "text-destructive",
  },
  bg: {
    primary: "bg-background",
    secondary: "bg-muted",
    accent: "bg-primary",
    success: "bg-green-50",
    warning: "bg-yellow-50",
    error: "bg-red-50",
  },
} as const;

// Spacing utility functions
export const spacing = {
  section: "mb-8",
  card: "p-6",
  cardHeader: "p-6 pb-4",
  cardContent: "p-6 pt-0",
  cardFooter: "p-6 pt-0",
} as const;

// Border radius utility
export const radius = {
  sm: "rounded-sm",
  default: "rounded-md",
  lg: "rounded-lg",
  xl: "rounded-xl",
  full: "rounded-full",
} as const;

// Shadow utility
export const shadow = {
  sm: "shadow-sm",
  default: "shadow",
  md: "shadow-md",
  lg: "shadow-lg",
  xl: "shadow-xl",
} as const;

// Typography helper functions
export function displayText(size: 'large' | 'default' = 'default') {
  return typography.display[size];
}

export function headingText(size: 'large' | 'default' | 'small' = 'default') {
  return typography.heading[size];
}

export function bodyText(size: 'large' | 'default' | 'small' = 'default') {
  return typography.body[size];
}

export function labelText() {
  return typography.label;
}

export function captionText() {
  return typography.caption;
}